import { PointerTracker } from '../gestures/pointer.js';

const SNAP_BACK_DURATION = 300; // ms
const SNAP_BACK_EASING = 'cubic-bezier(0.34, 1.56, 0.64, 1)'; // spring-like

export default function modal() {
    return {
        open: false,
        _tracker: null,
        _backdrop: null,
        _panel: null,
        _reducedMotion: false,

        show() {
            this.open = true;
            this.$nextTick(() => {
                this._initGestures();
            });
        },

        close() {
            this._destroyGestures();
            this.open = false;
        },

        // Done — close the modal and apply. The `filters-apply` event bubbles to
        // the landing form's listingFilter apply() (@filters-apply.window). Split
        // out of the template because the CSP Alpine build parses a single named
        // call per binding, not `close(); $dispatch(...)`.
        apply() {
            this.close();
            this.$dispatch('filters-apply');
        },

        // Reset — clear every control of the owning form and re-apply. The bar's
        // dropdowns share names with the modal's, so clearing only the modal's
        // (data-mf) re-submitted the bar's values and nothing changed (MOHO-161).
        // Same CSP-build reason: no inline forEach/$root in the template.
        reset() {
            const selects = this.$root.querySelectorAll('select[data-mf]');
            const controls = selects[0]?.form?.elements ?? selects;
            [...controls].forEach((el) => {
                if (el.name) el.value = '';
            });
            this.$dispatch('filters-apply');
        },

        _initGestures() {
            if (this._tracker) return;

            this._panel = this.$el.querySelector('[x-trap\\.inert\\.noscroll]');
            this._backdrop = this.$el.querySelector('[role="dialog"]')?.parentElement
                ?? this.$el.querySelector('.fixed');

            if (!this._panel) return;

            this._tracker = new PointerTracker(this._panel, {
                dismiss: { distanceRatio: 0.3, velocity: 0.8 },

                onDismissMove: (dy, progress) => {
                    if (dy <= 0) return;
                    this._panel.style.transition = 'none';
                    this._panel.style.transform = `translateY(${dy}px)`;

                    if (this._backdrop) {
                        this._backdrop.style.transition = 'none';
                        this._backdrop.style.opacity = 1 - progress * 0.6;
                    }
                },

                onDismiss: () => {
                    this.close();
                },

                onDismissCancel: () => {
                    const duration = this._reducedMotion ? 1 : SNAP_BACK_DURATION;
                    this._panel.style.transition =
                        `transform ${duration}ms ${SNAP_BACK_EASING}`;
                    this._panel.style.transform = 'translateY(0)';

                    if (this._backdrop) {
                        this._backdrop.style.transition =
                            `opacity ${duration}ms ${SNAP_BACK_EASING}`;
                        this._backdrop.style.opacity = 1;
                    }

                    // Clean up inline styles after transition
                    const cleanup = () => {
                        this._panel.style.transition = '';
                        this._panel.style.transform = '';
                        if (this._backdrop) {
                            this._backdrop.style.transition = '';
                            this._backdrop.style.opacity = '';
                        }
                        this._panel.removeEventListener('transitionend', cleanup);
                    };
                    this._panel.addEventListener('transitionend', cleanup, { once: true });
                },
            });
        },

        _destroyGestures() {
            if (this._tracker && !this._tracker.isNoop) {
                this._tracker.destroy();
                this._tracker = null;
            }
            // Reset any inline styles from gestures
            if (this._panel) {
                this._panel.style.transition = '';
                this._panel.style.transform = '';
            }
            if (this._backdrop) {
                this._backdrop.style.transition = '';
                this._backdrop.style.opacity = '';
            }
        },

        init() {
            this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        },

        destroy() {
            this._destroyGestures();
        },
    };
}
